/**
 * Architecture Diagram — React + Inline SVG
 *
 * A faithful recreation of the AI Application architecture from the reference
 * screenshot. Every node and edge is a discrete element with CSS-transitioned
 * fill/stroke/opacity so future animation (Framer Motion, GSAP, etc.) can
 * target individual pieces by data-attribute.
 *
 * Node-id mapping (SVG → use-case-data.ts):
 *   Person            → interface
 *   AI App            → agents
 *   Context Builder   → context
 *   Tool Search Tool  → (highlights with agents-tools connection)
 *   Instructions card → instructions
 *   Memory card       → memory
 *   Tools card        → tools
 *   Research Models   → research
 */

import React from 'react';

// ============================================
// Props
// ============================================

interface ArchDiagramSvgProps {
  highlightedNodes: Set<string>;
  highlightedConnections: Set<string>;
  /** Specific pill labels to highlight with secondary (amber) color */
  highlightedPills?: Set<string>;
  className?: string;
}

// ============================================
// Branding tokens (duplicated for compartmentalisation)
// ============================================

const C = {
  // --- normal state ---
  circleFill: '#D8F3ED',       // brand.primary
  circleStroke: '#B8E6DB',     // brand.primaryDark
  cardFill: '#F5FCFB',        // brand.extraLight
  cardStroke: '#B8E6DB',       // brand.primaryDark
  pillFill: '#EBF9F6',        // brand.primaryLight
  titleColor: '#334155',       // highlight.dark
  pillColor: '#475569',        // highlight.primary
  edgeColor: '#94A3B8',        // highlight.extraLight
  // --- highlighted state (coral) ---
  hlFill: '#FEEDED',           // accent.coral.exxtraLight
  hlStroke: '#DF6565',         // accent.coral.exxtraDark
  hlPill: '#FEE2E2',          // accent.coral.extraLight
  hlText: '#DF6565',           // accent.coral.exxtraDark
  hlEdge: '#DF6565',           // accent.coral.exxtraDark
  // --- secondary pill highlight (amber/gold — complements coral) ---
  hl2Pill: '#FEF3C7',         // accent.yellow.light
  hl2Text: '#C47E08',         // accent.yellow.exxtraDark
  hl2Stroke: '#F59E0B',       // accent.yellow.extraDark
} as const;

const FONT = {
  heading: "'Poppins', 'SF Pro Display', sans-serif",
  body: "'Poppins', 'SF Pro Text', sans-serif",
};

const T = 'all 0.4s cubic-bezier(0.25, 0.1, 0.25, 1)'; // transition shorthand

// ============================================
// Edge → use-case-data connection mapping
// ============================================

const EDGE_MAP: Record<string, string[]> = {
  'person-aiapp':          ['interface-agents'],
  'aiapp-instructions-in':  ['agents-instructions'],
  'aiapp-toolsearch':       ['agents-tools'],
  'toolsearch-tools':       ['agents-tools'],
  'aiapp-context-in':       ['agents-context'],
  'context-memory':         ['context-memory', 'agents-memory'],
  'context-research':       ['context-research', 'agents-research'],
  'research-aiapp':         ['agents-research'],
};

// ============================================
// Pill-rendering helper
// ============================================

const PILL_H = 22;
const PILL_RX = 11;
const PILL_GAP_Y = 5;
const PILL_GAP_X = 4;
const PILL_PAD = 10;
const PILL_FONT = 9.5;

function renderPillRows(
  rows: string[][],
  cardX: number,
  cardW: number,
  startY: number,
  hl: boolean,
  hlPills?: Set<string>,
) {
  const elements: React.ReactNode[] = [];
  rows.forEach((row, ri) => {
    const ry = startY + ri * (PILL_H + PILL_GAP_Y);
    const availW = cardW - 2 * PILL_PAD;
    const pw = (availW - (row.length - 1) * PILL_GAP_X) / row.length;
    row.forEach((label, ci) => {
      const px = cardX + PILL_PAD + ci * (pw + PILL_GAP_X);
      const isSecondary = hlPills?.has(label) ?? false;
      const pillFill = isSecondary ? C.hl2Pill : hl ? C.hlPill : C.pillFill;
      const pillText = isSecondary ? C.hl2Text : hl ? C.hlText : C.pillColor;
      const pillStroke = isSecondary ? C.hl2Stroke : 'none';
      elements.push(
        <g key={`pill-${ri}-${ci}`} data-pill={label}>
          <rect
            x={px}
            y={ry}
            width={pw}
            height={PILL_H}
            rx={PILL_RX}
            fill={pillFill}
            stroke={pillStroke}
            strokeWidth={isSecondary ? 1.5 : 0}
            style={{ transition: T }}
          />
          <text
            x={px + pw / 2}
            y={ry + PILL_H / 2 + 3.5}
            textAnchor="middle"
            fontSize={PILL_FONT}
            fontFamily={FONT.body}
            fontWeight={isSecondary ? 600 : 400}
            fill={pillText}
            style={{ transition: T }}
          >
            {label}
          </text>
        </g>,
      );
    });
  });
  return elements;
}

// ============================================
// Main Component
// ============================================

export const ArchDiagramSvg: React.FC<ArchDiagramSvgProps> = ({
  highlightedNodes,
  highlightedConnections,
  highlightedPills,
  className,
}) => {
  // ---- helpers ----
  const nhl = (id: string) => highlightedNodes.has(id);
  const ehl = (edgeId: string) =>
    EDGE_MAP[edgeId]?.some((c) => highlightedConnections.has(c)) ?? false;
  // tool-search highlights when the agents-tools connection is active
  const toolSearchHl =
    highlightedConnections.has('agents-tools') || nhl('tools');

  // ---- shorthand for edge styling ----
  const edgeStyle = (id: string) => {
    const hl = ehl(id);
    return {
      stroke: hl ? C.hlEdge : C.edgeColor,
      strokeWidth: hl ? 2 : 1.5,
      fill: 'none' as const,
      markerEnd: hl ? 'url(#ah-hl)' : 'url(#ah)',
      style: { transition: T },
    };
  };

  return (
    <svg
      viewBox="0 0 500 690"
      className={className}
      style={{ width: '100%', height: '100%' }}
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* ============================================ */}
      {/* DEFS: arrowhead markers                      */}
      {/* ============================================ */}
      <defs>
        <marker
          id="ah"
          viewBox="0 0 10 7"
          refX="9"
          refY="3.5"
          markerWidth="8"
          markerHeight="6"
          orient="auto"
        >
          <path d="M0 0 L10 3.5 L0 7z" fill={C.edgeColor} />
        </marker>
        <marker
          id="ah-hl"
          viewBox="0 0 10 7"
          refX="9"
          refY="3.5"
          markerWidth="8"
          markerHeight="6"
          orient="auto"
        >
          <path d="M0 0 L10 3.5 L0 7z" fill={C.hlEdge} />
        </marker>
        {/* Reversed arrowheads for markerStart (bidirectional edges) */}
        <marker
          id="ah-start"
          viewBox="0 0 10 7"
          refX="1"
          refY="3.5"
          markerWidth="8"
          markerHeight="6"
          orient="auto"
        >
          <path d="M10 0 L0 3.5 L10 7z" fill={C.edgeColor} />
        </marker>
        <marker
          id="ah-start-hl"
          viewBox="0 0 10 7"
          refX="1"
          refY="3.5"
          markerWidth="8"
          markerHeight="6"
          orient="auto"
        >
          <path d="M10 0 L0 3.5 L10 7z" fill={C.hlEdge} />
        </marker>
      </defs>

      {/* ============================================ */}
      {/* LAYER 1 — Edges                              */}
      {/* ============================================ */}
      <g data-layer="edges">
        {/* Person ↔ AI App (bidirectional) */}
        <path
          data-edge="person-aiapp"
          d="M 90,96 Q 115,165 150,212"
          {...edgeStyle('person-aiapp')}
          markerStart={ehl('person-aiapp') ? 'url(#ah-start-hl)' : 'url(#ah-start)'}
        />

        {/* Instructions → AI App */}
        <path
          data-edge="aiapp-instructions-in"
          d="M 248,137 Q 220,178 188,216"
          {...edgeStyle('aiapp-instructions-in')}
        />

        {/* Tool Search Tool → AI App */}
        <path
          data-edge="aiapp-toolsearch"
          d="M 309,280 L 210,252"
          {...edgeStyle('aiapp-toolsearch')}
        />

        {/* Context Builder → AI App */}
        <path
          data-edge="aiapp-context-in"
          d="M 148,373 Q 140,340 160,288"
          {...edgeStyle('aiapp-context-in')}
        />

        {/* Memory → Context Builder */}
        <path
          data-edge="context-memory"
          d="M 108,488 Q 115,458 132,432"
          {...edgeStyle('context-memory')}
        />

        {/* Research Models → Context Builder */}
        <path
          data-edge="context-research"
          d="M 278,568 Q 230,500 172,434"
          {...edgeStyle('context-research')}
        />

        {/* Tools → Tool Search Tool */}
        <path
          data-edge="toolsearch-tools"
          d="M 380,380 Q 368,352 358,326"
          {...edgeStyle('toolsearch-tools')}
        />

        {/* Research Models → AI App (routed through gap between Context and Tools) */}
        <path
          data-edge="research-aiapp"
          d="M 278,568 Q 250,430 200,288"
          {...edgeStyle('research-aiapp')}
        />
      </g>

      {/* ============================================ */}
      {/* LAYER 2 — Card nodes                         */}
      {/* ============================================ */}
      <g data-layer="cards">
        {/* ---- Instructions ---- */}
        <g data-node="instructions">
          <rect
            x={240}
            y={42}
            width={200}
            height={95}
            rx={12}
            fill={nhl('instructions') ? C.hlFill : C.cardFill}
            stroke={nhl('instructions') ? C.hlStroke : C.cardStroke}
            strokeWidth={1.5}
            style={{ transition: T }}
          />
          <text
            x={340}
            y={65}
            textAnchor="middle"
            fontSize={13}
            fontFamily={FONT.heading}
            fontWeight={600}
            fill={nhl('instructions') ? C.hlText : C.titleColor}
            style={{ transition: T }}
          >
            Instructions
          </text>
          {renderPillRows(
            [['Action Agents'], ['Interface Responses']],
            240,
            200,
            75,
            nhl('instructions'),
            highlightedPills,
          )}
        </g>

        {/* ---- Memory ---- */}
        <g data-node="memory">
          <rect
            x={10}
            y={488}
            width={200}
            height={175}
            rx={12}
            fill={nhl('memory') ? C.hlFill : C.cardFill}
            stroke={nhl('memory') ? C.hlStroke : C.cardStroke}
            strokeWidth={1.5}
            style={{ transition: T }}
          />
          <text
            x={110}
            y={512}
            textAnchor="middle"
            fontSize={13}
            fontFamily={FONT.heading}
            fontWeight={600}
            fill={nhl('memory') ? C.hlText : C.titleColor}
            style={{ transition: T }}
          >
            Memory
          </text>
          {renderPillRows(
            [
              ['Household Relationships'],
              ['Preferences', 'Contacts'],
              ['Facts', 'Reports', 'Chats'],
              ['Goals'],
            ],
            10,
            200,
            522,
            nhl('memory'),
            highlightedPills,
          )}
        </g>

        {/* ---- Tools ---- */}
        <g data-node="tools">
          <rect
            x={305}
            y={380}
            width={175}
            height={135}
            rx={12}
            fill={nhl('tools') ? C.hlFill : C.cardFill}
            stroke={nhl('tools') ? C.hlStroke : C.cardStroke}
            strokeWidth={1.5}
            style={{ transition: T }}
          />
          <text
            x={392}
            y={403}
            textAnchor="middle"
            fontSize={13}
            fontFamily={FONT.heading}
            fontWeight={600}
            fill={nhl('tools') ? C.hlText : C.titleColor}
            style={{ transition: T }}
          >
            Tools
          </text>
          {renderPillRows(
            [['Calendar', 'Projects'], ['Reminders', 'SMS'], ['Web Search']],
            305,
            175,
            413,
            nhl('tools'),
            highlightedPills,
          )}
        </g>

        {/* ---- Research Models ---- */}
        <g data-node="research">
          <rect
            x={220}
            y={568}
            width={210}
            height={110}
            rx={12}
            fill={nhl('research') ? C.hlFill : C.cardFill}
            stroke={nhl('research') ? C.hlStroke : C.cardStroke}
            strokeWidth={1.5}
            style={{ transition: T }}
          />
          <text
            x={325}
            y={590}
            textAnchor="middle"
            fontSize={13}
            fontFamily={FONT.heading}
            fontWeight={600}
            fill={nhl('research') ? C.hlText : C.titleColor}
            style={{ transition: T }}
          >
            <tspan x={325} dy="0">
              Research
            </tspan>
            <tspan x={325} dy="16">
              Models
            </tspan>
          </text>
          {renderPillRows(
            [['Financial', 'Health'], ['Education', 'Maintenance']],
            220,
            210,
            614,
            nhl('research'),
            highlightedPills,
          )}
        </g>
      </g>

      {/* ============================================ */}
      {/* LAYER 3 — Circle nodes                       */}
      {/* ============================================ */}
      <g data-layer="circles">
        {/* ---- Person ---- */}
        <g data-node="interface">
          <circle
            cx={75}
            cy={72}
            r={28}
            fill={nhl('interface') ? C.hlFill : C.circleFill}
            stroke={nhl('interface') ? C.hlStroke : C.circleStroke}
            strokeWidth={1.5}
            style={{ transition: T }}
          />
          <text
            x={75}
            y={77}
            textAnchor="middle"
            fontSize={12}
            fontFamily={FONT.heading}
            fontWeight={600}
            fill={nhl('interface') ? C.hlText : C.titleColor}
            style={{ transition: T }}
          >
            Person
          </text>
        </g>

        {/* ---- AI App (hub — larger) ---- */}
        <g data-node="agents">
          <circle
            cx={170}
            cy={250}
            r={40}
            fill={nhl('agents') ? C.hlFill : C.circleFill}
            stroke={nhl('agents') ? C.hlStroke : C.circleStroke}
            strokeWidth={2}
            style={{ transition: T }}
          />
          <text
            x={170}
            y={255}
            textAnchor="middle"
            fontSize={14}
            fontFamily={FONT.heading}
            fontWeight={600}
            fill={nhl('agents') ? C.hlText : C.titleColor}
            style={{ transition: T }}
          >
            AI App
          </text>
        </g>

        {/* ---- Context Builder ---- */}
        <g data-node="context">
          <circle
            cx={155}
            cy={405}
            r={33}
            fill={nhl('context') ? C.hlFill : C.circleFill}
            stroke={nhl('context') ? C.hlStroke : C.circleStroke}
            strokeWidth={1.5}
            style={{ transition: T }}
          />
          <text
            x={155}
            y={401}
            textAnchor="middle"
            fontSize={11}
            fontFamily={FONT.heading}
            fontWeight={600}
            fill={nhl('context') ? C.hlText : C.titleColor}
            style={{ transition: T }}
          >
            <tspan x={155} dy="0">
              Context
            </tspan>
            <tspan x={155} dy="14">
              Builder
            </tspan>
          </text>
        </g>

        {/* ---- Tool Search Tool ---- */}
        <g data-node="tool-search">
          <circle
            cx={345}
            cy={290}
            r={38}
            fill={toolSearchHl ? C.hlFill : C.circleFill}
            stroke={toolSearchHl ? C.hlStroke : C.circleStroke}
            strokeWidth={1.5}
            style={{ transition: T }}
          />
          <text
            x={345}
            y={286}
            textAnchor="middle"
            fontSize={12}
            fontFamily={FONT.heading}
            fontWeight={600}
            fill={toolSearchHl ? C.hlText : C.titleColor}
            style={{ transition: T }}
          >
            <tspan x={345} dy="0">
              Tool Search
            </tspan>
            <tspan x={345} dy="15">
              Tool
            </tspan>
          </text>
        </g>
      </g>
    </svg>
  );
};
